// Signalerna i den gemensamma datamodellen (kravspecen §4 och §6, och husen i b3).
// Varje komponent översätts av en adapter i navet till dessa signaler.
// En signal kan ha en instans, till exempel batteribanken eller motorn. Husens signaler har
// fria instanser, till exempel zonens eller rummets namn ("hus.zon.temp@Stue").

export type Vardetyp = "tal" | "bool" | "text" | "position";

export type Position = { lat: number; lon: number };

export type Varde = number | boolean | string | Position | null;

export interface SignalDef {
  enhet: string;
  typ: Vardetyp;
  beskrivning: string;
  /**
   * Tillåtna instanser. Tom lista betyder att signalen saknar instans. "fri": instansen får
   * saknas eller vara ett namn (FRI_INSTANS), till exempel en zon i huset.
   */
  instanser: readonly string[] | "fri";
  /** Tillåtna textvärden, för signaler av typen text med fasta lägen. */
  lagen?: readonly string[];
}

export const BANKAR = ["forbrukning", "navigation"] as const;
export const STARTBATTERIER = ["start_sb", "start_bb"] as const;
/**
 * Laddarna: Mastervolts (b1–b10), och b16 Victrons: en MultiPlus eller Quattro via GX (vebus), en
 * Orion DC/DC (dcdc) och en Blue Smart för 230 V (smartladdare).
 */
export const LADDARE = [
  "masscombi",
  "cm1225",
  "plus1250",
  "plusbb",
  "plussb",
  "vebus",
  "dcdc",
  "smartladdare",
] as const;
/**
 * Motorernas platser, som är motorernas instanser i signalerna (motor.varvtal@babord). b14: en
 * båt kan ha en motor per plats. b15: platsen är babord, styrbord eller mitten, och en generator
 * har platsen generator. Med ett nummer när flera delar plats: babord2 är den andra på babord,
 * till exempel fyra utombordare (babord, babord2, styrbord2 och styrbord). Högst fyra per plats.
 */
export const MOTORBASER = ["babord", "styrbord", "mitten", "generator"] as const;

export type Motorbas = (typeof MOTORBASER)[number];

/** Numren för motorer som delar plats: babord är nummer 1, babord2 nummer 2. */
export const MOTORNUMMER = [1, 2, 3, 4] as const;

export type Motor = Motorbas | `${Motorbas}${2 | 3 | 4}`;

/** Alla motorernas namn i signalerna, i ordning: babord, babord2, …, styrbord, …, generator4. */
export const MOTORER: readonly Motor[] = MOTORBASER.flatMap((b) =>
  MOTORNUMMER.map((n) => (n === 1 ? b : `${b}${n}`) as Motor),
);

/**
 * b14:s plats för en utombordare. Flytten i b15 gör den till en utombordare på mitten, men ett nav
 * från b14 kan sända den medan det uppdateras, och då sparas värdet som det är.
 */
export const GAMLA_MOTORER = ["utombordare"] as const;

/** Instanserna för motorernas signaler: motorerna, och b14:s plats för en utombordare. */
const MOTORINSTANSER: readonly string[] = [...MOTORER, ...GAMLA_MOTORER];

/** b15: förbrukarna som inte driver båten och inte är en motor: värmarna. */
export const FORBRUKARE = ["varmare", "varmare2", "varmare3", "varmare4"] as const;

export type Forbrukarnyckel = (typeof FORBRUKARE)[number];

/** En fri instans: 1–40 tecken, utan @ och utan styrtecken. */
export const FRI_INSTANS = /^[^@\p{Cc}]{1,40}$/u;

/** Husets läge: hemma eller borta (b3 avsnitt 3 B). */
export const HUSLAGEN = ["hemma", "borta"] as const;

/** Nobø-hubbens globala läge (API 1.1, Override Mode 0–3). */
export const NOBOLAGEN = ["normal", "komfort", "eko", "borta"] as const;

/** En zons läge just nu, efter veckoprogrammet eller ett globalt läge. */
export const ZONLAGEN = ["komfort", "eko", "borta", "av"] as const;

/**
 * b24: vad hubben kan ställa i en Nobø-zon, efter komponenternas modeller: båda temperaturerna
 * (eko_komfort), bara eko medan komforten ställs på elementet (eko), ingen av dem, till exempel i ett
 * rum med golvvärme (inget), eller okänt, när en modell inte är känd eller zonen saknar termostat.
 */
export const ZONSTYRNINGAR = ["eko_komfort", "eko", "inget", "okand"] as const;

/** Larmet i Home Assistant (alarm_control_panel). */
/**
 * Det som molnet kan styra på en plats: Nobø-hubben och Home Assistant (b3), och från b10
 * laddarna i vinterläget via MasterBus, för en båt (bara vitlistan, VINTERRADER).
 */
export const STYRMAL = ["nobo", "ha", "masterbus"] as const;

/**
 * b10: raderna i vitlistan för vinterläget, det enda som navet får skriva på MasterBus:
 * Charger i Masscombi och On/Standby i Plus 1250 (BK28).
 */
export const VINTERRADER = ["masscombi", "plus1250"] as const;

/** Scenerna i Home Assistant som molnet kan köra (b3-1). */
export const HASCENER = ["forvarm", "hemma", "borta"] as const;

export const LARMLAGEN = ["av", "hemma", "borta", "natt", "semester", "utlost", "okant"] as const;

/**
 * b12: autopilotens läge, ur PGN 127237 (Steering Mode) eller Raymarines egna PGN 65379 och
 * 126720 (Pilot Mode). Standby: rorsmannen styr. Manuell: styrning med autopilotens spak eller
 * fjärrkontroll. Auto, vind, spår och ingen drift: autopiloten styr själv.
 */
export const AUTOPILOTLAGEN = [
  "standby",
  "manuell",
  "auto",
  "vind",
  "spar",
  "ingen_drift",
  "okant",
] as const;

export const LADDLAGEN = [
  "av",
  "vila",
  "bulk",
  "absorption",
  "float",
  "laddar",
  "fel",
  "okant",
] as const;

export const SIGNALER = {
  // Batterierna (MasterBus: MLi6000, MLi1250 och CM12/25)
  "batteri.soc": {
    enhet: "%",
    typ: "tal",
    beskrivning: "Laddning",
    instanser: BANKAR,
  },
  "batteri.spanning": {
    enhet: "V",
    typ: "tal",
    beskrivning: "Spänning",
    instanser: [...BANKAR, ...STARTBATTERIER],
  },
  "batteri.strom": {
    enhet: "A",
    typ: "tal",
    beskrivning: "Ström, positiv vid laddning",
    instanser: BANKAR,
  },
  "batteri.temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Batteriets temperatur",
    instanser: BANKAR,
  },
  "batteri.brytare_sluten": {
    enhet: "",
    typ: "bool",
    beskrivning: "Huvudreläet eller huvudbrytaren är sluten",
    instanser: BANKAR,
  },
  // b16: Victrons batteriövervakare (SmartShunt, BMV och Lynx Smart BMS): effekten och tiden kvar.
  "batteri.effekt": {
    enhet: "W",
    typ: "tal",
    beskrivning: "Effekt, positiv vid laddning",
    instanser: BANKAR,
  },
  "batteri.tid_kvar": {
    enhet: "h",
    typ: "tal",
    beskrivning: "Tiden kvar med strömmen nu, enligt batteriövervakaren",
    instanser: BANKAR,
  },

  // b16: solcellerna, Victrons solladdare (SmartSolar MPPT) via GX eller Bluetooth, för alla
  // solladdare tillsammans.
  "sol.effekt": {
    enhet: "W",
    typ: "tal",
    beskrivning: "Solcellernas effekt, alla solladdare",
    instanser: [],
  },
  "sol.skord": {
    enhet: "kWh",
    typ: "tal",
    beskrivning: "Solcellernas skörd i dag, alla solladdare",
    instanser: [],
  },
  "sol.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Solladdarens läge",
    instanser: [],
    lagen: LADDLAGEN,
  },
  "victron.kontakt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Navet får svar från Victrons GX-enhet",
    instanser: [],
  },
  // b17: inloggningen på GX-enheten (MQTT): utan lösenord, lyckades, fel lösenord, eller att
  // GX-enheten kräver ett lösenord som navet inte har. Aldrig lösenordet.
  "victron.inloggning": {
    enhet: "",
    typ: "text",
    beskrivning: "Inloggningen på Victrons GX-enhet",
    instanser: [],
    lagen: ["utan", "lyckades", "fel", "kravs"],
  },
  // b17: Homey Pro, bara läsning: kontakten, nyckeln (saknas, fel eller finns), nyckelns
  // behörigheter (bara läsning, mer än läsning, eller okänt), och att en Homey svarar på mDNS.
  "homey.kontakt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Navet får svar från Homey",
    instanser: [],
  },
  "homey.nyckel": {
    enhet: "",
    typ: "text",
    beskrivning: "Nyckeln för Homey i navet",
    instanser: [],
    lagen: ["saknas", "fel", "finns"],
  },
  "homey.behorighet": {
    enhet: "",
    typ: "text",
    beskrivning: "Behörigheterna för nyckeln till Homey",
    instanser: [],
    lagen: ["bara_lasning", "mer_an_lasning", "okand"],
  },
  "homey.forslag": {
    enhet: "",
    typ: "bool",
    beskrivning: "En Homey svarar på mDNS på platsens nät",
    instanser: [],
  },

  // Landströmmen (Mass GI 3.5)
  "landstrom.finns": { enhet: "", typ: "bool", beskrivning: "Landström finns", instanser: [] },
  "landstrom.spanning": {
    enhet: "V",
    typ: "tal",
    beskrivning: "Landströmmens spänning",
    instanser: [],
  },
  "landstrom.strom": { enhet: "A", typ: "tal", beskrivning: "Landströmmens ström", instanser: [] },
  "landstrom.effekt": {
    enhet: "W",
    typ: "tal",
    beskrivning: "Landströmmens effekt",
    instanser: [],
  },

  // Laddning och växelriktare (Masscombi, CM12/25 och DC/DC-laddarna)
  "laddare.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Laddarens läge",
    instanser: LADDARE,
    lagen: LADDLAGEN,
  },
  "laddare.strom": {
    enhet: "A",
    typ: "tal",
    beskrivning: "Laddarens ström ut",
    instanser: LADDARE,
  },
  "vaxelriktare.pa": { enhet: "", typ: "bool", beskrivning: "Växelriktaren är på", instanser: [] },
  "ac.kalla": {
    enhet: "",
    typ: "text",
    beskrivning: "Varifrån 230 V kommer",
    instanser: [],
    lagen: ["land", "generator", "vaxelriktare", "ingen", "okant"],
  },
  "ac.effekt": {
    enhet: "W",
    typ: "tal",
    beskrivning: "Effekt på Masscombis AC-utgång",
    instanser: ["ut1", "ut2"],
  },

  // Generatorn (MasterBus-gränssnittet för Whisper)
  "generator.gar": { enhet: "", typ: "bool", beskrivning: "Generatorn går", instanser: [] },
  // b15: en förbrukare som inte driver båten går, till exempel värmaren. Navet sätter den ur
  // signalen som ägaren har kopplat, till exempel en entitet i Home Assistant.
  "forbrukare.gar": {
    enhet: "",
    typ: "bool",
    beskrivning: "Förbrukaren går, enligt signalen som ägaren har kopplat",
    instanser: FORBRUKARE,
  },
  // b16: temperaturen vid förbrukarens utblås, från en givare (till exempel en Ruuvi) eller en
  // entitet i Home Assistant. Navet härleder forbrukare.gar ur den och en jämförelse.
  "forbrukare.utblas": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperaturen vid förbrukarens utblås",
    instanser: FORBRUKARE,
  },
  "generator.fel": {
    enhet: "",
    typ: "text",
    beskrivning: "Generatorns senaste fel: inget, kommunikation eller kod N",
    instanser: [],
  },

  // Kontakten med Mastervolt (adaptern själv)
  "mastervolt.kontakt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Navet får svar från Mastervolt",
    instanser: [],
  },
  "mastervolt.saknas": {
    enhet: "",
    typ: "text",
    beskrivning: "Enheter på MasterBus som inte svarar, kommaseparerade",
    instanser: [],
  },
  "mastervolt.ramfel": {
    enhet: "",
    typ: "text",
    beskrivning:
      "Fel i navets egen kod: gränssnittet har svarat med felkod 01–04 (fel i ramen). Tomt om inget",
    instanser: [],
  },
  // b9: MasterBus direkt via CAN. Läget: lyssnar, frågar, eller frågorna stoppade efter ett fel
  // på bussen.
  "masterbus.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "MasterBus via CAN: om navet bara lyssnar eller frågar, och fel på bussen",
    instanser: [],
  },
  // b14: motorernas instanser som navet hör, för förslaget till båtens uppställning.
  "nmea2000.motorinstanser": {
    enhet: "",
    typ: "text",
    beskrivning:
      "Motorernas instanser som navet hör på NMEA 2000 (127488, 127489 och 127497), kommaseparerade",
    instanser: [],
  },
  // b9: NMEA 2000 via CAN: om navet hör bussen.
  "nmea2000.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "NMEA 2000 via CAN: om navet hör bussen, och fel på bussen",
    instanser: [],
  },

  // NMEA 2000
  position: { enhet: "lat/lon", typ: "position", beskrivning: "Position", instanser: [] },
  "instrument.fart_kn": { enhet: "kn", typ: "tal", beskrivning: "Fart över grund", instanser: [] },
  "instrument.kurs_grader": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Kurs över grund",
    instanser: [],
  },
  "instrument.djup_m": { enhet: "m", typ: "tal", beskrivning: "Djup under givaren", instanser: [] },
  "instrument.vind_ms": {
    enhet: "m/s",
    typ: "tal",
    beskrivning: "Vind (skenbar) från båtens givare",
    instanser: [],
  },
  "instrument.vind_vinkel": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Vindens vinkel mot fören (skenbar)",
    instanser: [],
  },
  "vatten.temp": { enhet: "°C", typ: "tal", beskrivning: "Vattnets temperatur", instanser: [] },
  "motor.varvtal": { enhet: "rpm", typ: "tal", beskrivning: "Varvtal", instanser: MOTORINSTANSER },
  "motor.kylvatten_temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Kylvattnets temperatur",
    instanser: MOTORINSTANSER,
  },
  "motor.oljetryck": {
    enhet: "kPa",
    typ: "tal",
    beskrivning: "Oljetryck",
    instanser: MOTORINSTANSER,
  },
  "motor.timmar": { enhet: "h", typ: "tal", beskrivning: "Gångtimmar", instanser: MOTORINSTANSER },
  // b12: mer ur motorernas PGN 127489, och attityden, kursen och autopiloten. Bara läsning.
  "motor.bransle": {
    enhet: "l/h",
    typ: "tal",
    beskrivning: "Bränsleflöde",
    instanser: MOTORINSTANSER,
  },
  "motor.laddning": {
    enhet: "V",
    typ: "tal",
    beskrivning: "Generatorns spänning på motorn",
    instanser: MOTORINSTANSER,
  },
  "motor.last": { enhet: "%", typ: "tal", beskrivning: "Motorns last", instanser: MOTORINSTANSER },
  "motor.status": {
    enhet: "",
    typ: "text",
    beskrivning:
      "Motorns egna varningar och lägen (statusbitarna i PGN 127489), med canboats namn och kommaseparerade. Tom när inget är satt",
    instanser: MOTORINSTANSER,
  },
  "bat.trim": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Trimvinkeln (pitch), positiv med fören upp",
    instanser: [],
  },
  "bat.slagsida": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Slagsidan (roll), positiv när båten lutar åt styrbord",
    instanser: [],
  },
  "bat.kurs": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Kursen som stäven pekar, från kompassen (PGN 127250)",
    instanser: [],
  },
  "bat.girhastighet": {
    enhet: "°/s",
    typ: "tal",
    beskrivning: "Girhastigheten, positiv åt styrbord",
    instanser: [],
  },
  "autopilot.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Autopilotens läge, som autopiloten säger det",
    instanser: [],
    lagen: AUTOPILOTLAGEN,
  },
  "autopilot.kurs": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Autopilotens styrkurs",
    instanser: [],
  },
  // b13: motorernas egen resedata (PGN 127496 och 127497), som Volvos EVC räknar den. Bara
  // läsning. Fälten och enheterna är canboats.
  "tripp.tid_till_tom": {
    enhet: "h",
    typ: "tal",
    beskrivning: "Tiden till tom tank, enligt motorerna (PGN 127496)",
    instanser: [],
  },
  "tripp.nm_till_tom": {
    enhet: "NM",
    typ: "tal",
    beskrivning: "Sträckan till tom tank, enligt motorerna (PGN 127496)",
    instanser: [],
  },
  "tripp.bransle_kvar": {
    enhet: "l",
    typ: "tal",
    beskrivning: "Bränslet kvar, uppskattat av motorerna (PGN 127496)",
    instanser: [],
  },
  "tripp.tid": {
    enhet: "h",
    typ: "tal",
    beskrivning: "Trippens tid, enligt motorerna (PGN 127496)",
    instanser: [],
  },
  "motor.tripp_liter": {
    enhet: "l",
    typ: "tal",
    beskrivning: "Bränslet under trippen, enligt motorn (PGN 127497)",
    instanser: MOTORINSTANSER,
  },
  "motor.tripp_medel": {
    enhet: "l/h",
    typ: "tal",
    beskrivning: "Medelförbrukningen under trippen, enligt motorn (PGN 127497)",
    instanser: MOTORINSTANSER,
  },
  "motor.tripp_ekonomi": {
    enhet: "l/h",
    typ: "tal",
    beskrivning: "Fuel Rate, Economy i PGN 127497, med canboats enhet",
    instanser: MOTORINSTANSER,
  },
  "motor.tripp_ogonblick": {
    enhet: "l/h",
    typ: "tal",
    beskrivning: "Instantaneous Fuel Economy i PGN 127497, med canboats enhet",
    instanser: MOTORINSTANSER,
  },

  // Ingångar och trådlösa givare (i b1 från simulatorn)
  "lanspump.gar": { enhet: "", typ: "bool", beskrivning: "Länspumpen går", instanser: [] },
  "kolsvin.niva": {
    enhet: "",
    typ: "text",
    beskrivning: "Nivån i kölsvinet",
    instanser: [],
    lagen: ["lag", "hog"],
  },
  "kabin.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur i kabinen", instanser: [] },
  "kabin.fukt": { enhet: "%", typ: "tal", beskrivning: "Fukt i kabinen", instanser: [] },
  "motorrum.temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperatur i motorrummet",
    instanser: [],
  },
  "ute.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur ute", instanser: [] },
  // b16: varifrån temperaturen och fukten ute kommer, till exempel "Ecowitt GW2000A" eller
  // "Home Assistant: sensor.ute_temperatur". Instansen är temp eller fukt. b20-1: station är
  // källan för ute.temp och hus källan för hus.ute.temp, var för sig (ute.ts). temp skickas inte
  // längre, men läses från ett äldre nav.
  "ute.kalla": {
    enhet: "",
    typ: "text",
    beskrivning: "Varifrån temperaturen eller fukten ute kommer",
    instanser: ["temp", "fukt", "station", "hus"],
  },
  // b8: Ruuvi-givarna ombord: kabinen, motorrummet, ute och batteriutrymmet.
  "motorrum.fukt": { enhet: "%", typ: "tal", beskrivning: "Fukt i motorrummet", instanser: [] },
  "ute.fukt": { enhet: "%", typ: "tal", beskrivning: "Fukt ute", instanser: [] },
  "batterirum.temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperatur i batteriutrymmet",
    instanser: [],
  },
  "batterirum.fukt": {
    enhet: "%",
    typ: "tal",
    beskrivning: "Fukt i batteriutrymmet",
    instanser: [],
  },
  // b13: väderstationen, Ecowitt via platsens gateway eller BTHome (Shellys WS90). Allt i SI:
  // m/s, mm, hPa och °C. Temperaturen och fukten ute är ute.temp och ute.fukt, som förut.
  "vader.vind": {
    enhet: "m/s",
    typ: "tal",
    beskrivning: "Medelvinden vid väderstationen",
    instanser: [],
  },
  "vader.byvind": {
    enhet: "m/s",
    typ: "tal",
    beskrivning: "Byvinden vid väderstationen",
    instanser: [],
  },
  "vader.riktning": {
    enhet: "°",
    typ: "tal",
    beskrivning: "Varifrån det blåser, vid väderstationen",
    instanser: [],
  },
  "vader.regn": { enhet: "mm/h", typ: "tal", beskrivning: "Regnets intensitet", instanser: [] },
  "vader.regn_timme": {
    enhet: "mm",
    typ: "tal",
    beskrivning: "Regnet den senaste timmen",
    instanser: [],
  },
  "vader.regn_dygn": {
    enhet: "mm",
    typ: "tal",
    beskrivning: "Regnet i dag, från midnatt",
    instanser: [],
  },
  "vader.regn_handelse": {
    enhet: "mm",
    typ: "tal",
    beskrivning: "Regnet sedan det började regna (händelsen)",
    instanser: [],
  },
  "vader.regnar": {
    enhet: "",
    typ: "bool",
    beskrivning: "Det regnar, enligt den haptiska regngivaren",
    instanser: [],
  },
  "vader.tryck": {
    enhet: "hPa",
    typ: "tal",
    beskrivning: "Lufttrycket, relativt (vid havsytan)",
    instanser: [],
  },
  "vader.tryck_abs": {
    enhet: "hPa",
    typ: "tal",
    beskrivning: "Lufttrycket, absolut",
    instanser: [],
  },
  "vader.ljus": { enhet: "W/m²", typ: "tal", beskrivning: "Solinstrålningen", instanser: [] },
  "vader.uv": { enhet: "", typ: "tal", beskrivning: "UV-index", instanser: [] },
  "vader.daggpunkt": { enhet: "°C", typ: "tal", beskrivning: "Daggpunkten", instanser: [] },
  "vader.inne_temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperaturen vid väderstationens gateway, inne",
    instanser: [],
  },
  "vader.inne_fukt": {
    enhet: "%",
    typ: "tal",
    beskrivning: "Fukten vid väderstationens gateway, inne",
    instanser: [],
  },
  "vader.kondensator": {
    enhet: "V",
    typ: "tal",
    beskrivning: "Spänningen i WS90:s superkondensator",
    instanser: [],
  },
  "vader.kalla": {
    enhet: "",
    typ: "text",
    beskrivning: "Väderstationen: Ecowitt med gatewayens modell, eller BTHome",
    instanser: [],
  },
  "vader.batteri_lagt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Batteriet är lågt i en av väderstationens givare, per givare",
    instanser: "fri",
  },
  // b14: jordfukten från Ecowitts givare, per kanal (kanal1–kanal8), och givarens namn i Home
  // Assistant.
  "jord.fukt": {
    enhet: "%",
    typ: "tal",
    beskrivning: "Jordfukten, per givare (kanal1–kanal8)",
    instanser: "fri",
  },
  "jord.namn": {
    enhet: "",
    typ: "text",
    beskrivning: "Jordgivarens namn i Home Assistant, per kanal",
    instanser: "fri",
  },
  // b8: givarnas hälsa, per givare. Instansen är givarens id i navet, aldrig MAC-adressen.
  // När givaren hördes senast är tiden för dess senaste värde.
  "givare.batteri": {
    enhet: "V",
    typ: "tal",
    beskrivning: "Givarens batterispänning",
    instanser: "fri",
  },
  "givare.signal": {
    enhet: "dBm",
    typ: "tal",
    beskrivning: "Givarens signalstyrka hos navet",
    instanser: "fri",
  },
  // b9: batteriet i procent, för BTHome och Inkbird.
  "givare.batteri_procent": {
    enhet: "%",
    typ: "tal",
    beskrivning: "Givarens batteri i procent",
    instanser: "fri",
  },
  // b9: kylskåpet, frysen och givaren mot skrovets insida (badtemperaturen, uppskattad).
  "kyl.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur i kylskåpet", instanser: [] },
  "frys.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur i frysen", instanser: [] },
  "skrov.temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperatur mot skrovets insida, under vattenlinjen",
    instanser: [],
  },

  // Husen (b3): Home Assistant och Nobø. Zonernas och rummens namn är fria instanser.
  "hus.inne.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur inne", instanser: [] },
  "hus.ute.temp": { enhet: "°C", typ: "tal", beskrivning: "Temperatur ute", instanser: [] },
  "hus.fukt": { enhet: "%", typ: "tal", beskrivning: "Fukt inne", instanser: "fri" },
  "hus.effekt": { enhet: "W", typ: "tal", beskrivning: "Effekt", instanser: "fri" },
  "hus.energi": {
    enhet: "kWh",
    typ: "tal",
    beskrivning: "Energi, mätarställning",
    instanser: "fri",
  },
  "hus.vattenlacka": { enhet: "", typ: "bool", beskrivning: "Vattenläcka", instanser: "fri" },
  "hus.dorr": { enhet: "", typ: "bool", beskrivning: "Dörren är öppen", instanser: "fri" },
  // b17: förmågorna i Home Assistant och Homey: fönstren, låsen och brandvarnarna.
  "hus.fonster": { enhet: "", typ: "bool", beskrivning: "Fönstret är öppet", instanser: "fri" },
  "hus.las": { enhet: "", typ: "bool", beskrivning: "Låset är låst", instanser: "fri" },
  // b20-2 (BK52): ytterdörrens lås, via Home Assistant på Pi:n (paket/ha/src/las.ts). Bara läsning:
  // låst, olåst eller regeln ute (säkerhetslåst), dörren, batteriet och kontakten med låset.
  "las.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Ytterdörrens lås: låst, olåst eller regeln ute",
    instanser: [],
    lagen: ["last", "olast", "regel", "okand"],
  },
  "dorr.oppen": { enhet: "", typ: "bool", beskrivning: "Ytterdörren står öppen", instanser: [] },
  "dorr.av": {
    enhet: "",
    typ: "bool",
    beskrivning:
      "Ytterdörren läses inte (dörrsensorn är av på platsen): dorr.oppen är då inte aktuell",
    instanser: [],
  },
  "las.batteri": { enhet: "%", typ: "tal", beskrivning: "Låsets batteri", instanser: [] },
  "las.kontakt": {
    enhet: "",
    typ: "text",
    beskrivning: "Kontakten med låset: borta när låset inte har nåtts på 30 minuter",
    instanser: [],
    lagen: ["ok", "borta"],
  },
  "hus.rok": { enhet: "", typ: "bool", beskrivning: "Brandvarnaren larmar", instanser: "fri" },
  "hus.larm": {
    enhet: "",
    typ: "text",
    beskrivning: "Larmets läge",
    instanser: [],
    lagen: LARMLAGEN,
  },
  "hus.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Husets läge: hemma eller borta",
    instanser: [],
    lagen: HUSLAGEN,
  },
  "hus.zon.temp": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Temperatur i zonen, där en enhet rapporterar den",
    instanser: "fri",
  },
  "hus.zon.komfort": {
    enhet: "°C",
    typ: "tal",
    beskrivning: "Zonens komforttemperatur",
    instanser: "fri",
  },
  "hus.zon.eko": { enhet: "°C", typ: "tal", beskrivning: "Zonens ekotemperatur", instanser: "fri" },
  "hus.zon.program": {
    enhet: "",
    typ: "text",
    beskrivning: "Zonens veckoprogram",
    instanser: "fri",
  },
  "hus.zon.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Zonens läge just nu",
    instanser: "fri",
    lagen: ZONLAGEN,
  },
  "hus.zon.hubbstyrning": {
    enhet: "",
    typ: "text",
    beskrivning:
      "Vad Nobø-hubben kan ställa i zonen, efter komponenternas modeller: eko och komfort, bara eko, eller inget (golvvärme eller termostat på elementet)",
    instanser: "fri",
    lagen: ZONSTYRNINGAR,
  },
  "nobo.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Nobø-hubbens globala läge",
    instanser: [],
    lagen: NOBOLAGEN,
  },
  "nobo.kontakt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Navet får svar från Nobø-hubben",
    instanser: [],
  },
  "nobo.komponenter": {
    enhet: "",
    typ: "text",
    beskrivning: "Nobø-hubbens komponenter och deras zoner",
    instanser: [],
  },
  "ha.kontakt": {
    enhet: "",
    typ: "bool",
    beskrivning: "Navet får svar från Home Assistant",
    instanser: [],
  },
  // b3-1: den andra spärren. Navet säger om styrningen är på i dess egna inställningar, per mål.
  "styrning.platsen": {
    enhet: "",
    typ: "bool",
    beskrivning: "Styrningen är på i navets inställningar på platsen",
    instanser: STYRMAL,
  },
  // b10: vinterläget. Laddarnas variabler i vitlistan, som navet läser, och navets läge.
  "laddare.pa": {
    enhet: "",
    typ: "bool",
    beskrivning:
      "Laddaren är på enligt variabeln som vinterläget skriver: Charger i Masscombi, On/Standby i Plus 1250",
    instanser: VINTERRADER,
  },
  "vinter.lage": {
    enhet: "",
    typ: "text",
    beskrivning: "Vinterläget i navet: av, på med gränserna, eller varför det är stoppat",
    instanser: [],
  },
  "vinter.aktiv": {
    enhet: "",
    typ: "bool",
    beskrivning: "Vinterläget är på i navet, eller en laddare är av efter en skrivning från navet",
    instanser: [],
  },
  "vinter.skrivning": {
    enhet: "",
    typ: "text",
    beskrivning:
      "En skrivning som navet gjorde själv (vinterläget eller den säkra återgången), med värdet som lästes tillbaka",
    instanser: VINTERRADER,
  },
  "masterbus.skriv": {
    enhet: "",
    typ: "bool",
    beskrivning:
      "Navet kan skriva vitlistan: spärren MASTERBUS_SKRIV är på, frågorna är på och can1 är inte i listen-only",
    instanser: [],
  },
  "masterbus.skriv_orsak": {
    enhet: "",
    typ: "text",
    beskrivning: "Varför navet inte kan skriva vitlistan, eller tom",
    instanser: [],
  },
  // b11: att enheten på adressen är just den i vitlistan, enligt BusID.
  "vinter.identitet": {
    enhet: "",
    typ: "text",
    beskrivning:
      "Varför navet inte skriver till enheten: BusID och det navet hör på bussen säger inte att det är just den enheten i vitlistan. Tom när det stämmer",
    instanser: VINTERRADER,
  },
  // b10: tankarna via NMEA 2000 (PGN 127505). Instansen är tankens typ, till exempel diesel eller
  // svartvatten, och en siffra efter för fler tankar av samma typ (diesel2).
  "tank.niva": { enhet: "%", typ: "tal", beskrivning: "Tankens nivå", instanser: "fri" },
  "tank.volym": {
    enhet: "l",
    typ: "tal",
    beskrivning: "Det som finns i tanken, ur nivån och tankens volym",
    instanser: "fri",
  },
  // b13: tankens volym som tankgivaren säger den (Capacity i PGN 127505). Molnet föreslår den
  // som tankens volym, om ägaren inte har ställt in någon.
  "tank.kapacitet": {
    enhet: "l",
    typ: "tal",
    beskrivning: "Tankens volym enligt tankgivaren (Capacity i PGN 127505)",
    instanser: "fri",
  },
  // b4: navet säger vilka scener som är valda i dess inställningar, på samma sätt som spärren.
  "styrning.scen": {
    enhet: "",
    typ: "bool",
    beskrivning: "Scenen är vald i navets inställningar",
    instanser: HASCENER,
  },
} as const satisfies Record<string, SignalDef>;

export type SignalNamn = keyof typeof SIGNALER;

export function arSignal(namn: string): namn is SignalNamn {
  return Object.hasOwn(SIGNALER, namn);
}

/** Nyckeln för en signal med instans, till exempel "batteri.soc@forbrukning". */
export function signalnyckel(signal: string, instans: string | null): string {
  return instans ? `${signal}@${instans}` : signal;
}

/** Kontrollerar att ett värde passar signalens typ och instans. Returnerar ett fel eller null. */
export function kontrolleraVarde(
  signal: string,
  instans: string | null,
  varde: Varde,
): string | null {
  if (!arSignal(signal)) return `okänd signal: ${signal}`;
  const def: SignalDef = SIGNALER[signal];
  if (def.instanser === "fri") {
    if (instans !== null && !FRI_INSTANS.test(instans)) return `${signal} har ogiltig instans`;
  } else if (def.instanser.length === 0 && instans !== null) {
    return `${signal} har ingen instans`;
  } else if (def.instanser.length > 0 && (instans === null || !def.instanser.includes(instans))) {
    return `${signal} har okänd instans: ${instans}`;
  }
  if (varde === null) return null;
  switch (def.typ) {
    case "tal":
      return typeof varde === "number" && Number.isFinite(varde)
        ? null
        : `${signal} ska vara ett tal`;
    case "bool":
      return typeof varde === "boolean" ? null : `${signal} ska vara ja eller nej`;
    case "text":
      if (typeof varde !== "string") return `${signal} ska vara text`;
      if (def.lagen && !def.lagen.includes(varde)) return `${signal} har okänt läge: ${varde}`;
      return null;
    case "position":
      return typeof varde === "object" &&
        Number.isFinite(varde.lat) &&
        Number.isFinite(varde.lon) &&
        Math.abs(varde.lat) <= 90 &&
        Math.abs(varde.lon) <= 180
        ? null
        : `${signal} ska vara en position`;
  }
}
